<template>
  <div
    v-if="showCarousel"
    ref="carousel"
    class="ns-carousel"
    :class="{
      'ns-carousel-multiple': multiple,
      'ns-carousel-vertical': vertical,
    }"
  >
    <div
      v-if="showControls"
      class="ns-carousel-left ns-carousel-control"
      :disabled="disablePrevious"
      @click="previous"
    >
      <!-- @slot The 'left' slot is to override the left control icon. -->
      <slot name="left">
        <NsIcon icon-name="icon-chevron-left" size="icon-lg" />
      </slot>
    </div>

    <!-- This div is a window over visible content and holds all the pointer
      events -->
    <div
      class="ns-carousel-content-window"
      :class="{
        'ns-carousel-grabbing': grabbing,
      }"
      @mousemove="handleMouseMove"
      @mousedown="handleMouseDown"
      @mouseup="handleMouseUp"
      @mouseleave="handleMouseLeave"
      @touchmove="handleTouchMove"
      @touchend="handleTouchEnd"
    >
      <!-- This div has all the sizing logic and extra hidden padding for
        content overflow, but no pointer events so that extra padding is truly
        invisible-->
      <div ref="content" class="ns-carousel-content" :style="contentStyle">
        <NsCarouselItems
          :spacing="spacing"
          :vertical="vertical"
          @items-updated="_updateContentWidth"
        >
          <!-- @slot The default slot is a list of dom elements that need to be
            in a carousel. -->
          <slot />
        </NsCarouselItems>
      </div>
    </div>

    <div
      v-if="showControls"
      class="ns-carousel-right ns-carousel-control"
      :disabled="disableNext"
      @click="next"
    >
      <!-- @slot The 'right' slot is to override the right control icon. -->
      <slot name="right">
        <NsIcon icon-name="icon-chevron-right" size="icon-lg" />
      </slot>
    </div>
  </div>
</template>

<script>
/**
 * Original Author: Kody Crossman
 * Original Team: BFFs
 */
import { NsIcon } from '@nuskin/ns-icon';
import NsCarouselItems from './NsCarouselItems.vue';

/**
 * NsCarousel
 *
 * How to use: slot in a list of dom elements that need to be in a carousel.
 * Avoid usage of margins and use padding instead for the outer-most dom
 * element.
 *
 * @event position-changed When the current position of the carousel changes,
 * this event emits a number.
 * @event max-position-changed When the max position of the carousel changes,
 * this event emits a number
 */
export default {
  name: 'NsCarousel',
  components: { NsIcon, NsCarouselItems },
  props: {
    /**
     * This is the minimum number of children to display in the carousel. Must
     * be at least 1.
     */
    min: {
      type: Number,
      default: 1,
    },
    /**
     * This is the maximum number of children to display in the carousel. Can be
     * undefined.
     */
    max: {
      type: Number,
      default: undefined,
    },
    /**
     * Adds spacing (in pixels) between items.
     */
    spacing: {
      type: Number,
      default: 0,
    },
    /**
     * This is to override the current position of the carousel.
     */
    position: {
      type: Number,
      default: 0,
    },
    /**
     * This is to allow the carousel to wrap when at either the first or last
     * position. It will wrap by resetting to the opposite position to avoid
     * complexity.
     */
    wrap: Boolean,
    /**
     * This enables the carousel to go automatically move to the next position
     * on an interval (see: autoplayInterval). Setting this overrides the
     * carousel to wrap.
     */
    autoplay: Boolean,
    /**
     * This changes the autoplay interval in milliseconds when autoplay is
     * enabled.
     */
    autoplayInterval: {
      type: Number,
      default: 5000,
    },
    /**
     * This hides the carousel controls (i.e. left and right arrows).
     */
    hideControls: Boolean,
    /**
     * This disables the mouse dragging functionality.
     */
    disableMouseDrag: Boolean,
    /**
     * This disables the touch dragging functionality.
     */
    disableTouchDrag: Boolean,
    /**
     * This adds a small width to the end of the carousel to make it more
     * obvious that there are more items in the carousel.
     */
    peek: Boolean,
    /**
     * TODO! This is to override the content width to show a full width carousel
     * (negating min/max). The intention of this setting is to extend usage to
     * other components like the hero carousel, but is not fully implemented at
     * this time.
     */
    fullWidth: Boolean,
    /**
     * This is to change the orientation of the carousel to be vertical instead
     * of horitzontal.
     */
    vertical: Boolean,
    /**
     * The amount an item should be able to overflow vertically (Used for
     * Product Card Action visibilty)
     */
    verticalOverflowAmount: {
      type: Number,
      default: undefined,
    },
    uniqueResetKey: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      autoplayIntervalId: undefined,
      autoplayNext: this.autoplay,
      busy: false,
      childrenDisplayed: this.min,
      contentObserver: null,
      contentWidth: 0,
      currentPosition: 0,
      grabbing: false,
      maxChildrenDisplayed: this.max,
      maxPosition: 0,
      minChildrenDisplayed: this.min,
      movement: 0,
      mousePressed: false,
      mousePositionX: 0,
      mousePositionY: 0,
      previousTouch: null,
      showCarousel: true,
      showControls: !this.hideControls,
      slideIncrement: 0,
      wrapChildren: this.wrap,
    };
  },
  computed: {
    multiple() {
      return this.childrenDisplayed > 1;
    },
    contentStyle() {
      const contentWidth = this.fullWidth ? '100%' : `${this.contentWidth}px`;
      return {
        'max-width': contentWidth,
        width: contentWidth,
        'min-width': contentWidth,
        padding: `${this.verticalOverflowAmount || 0}px 0`,
        margin: `-${this.verticalOverflowAmount || 0}px 0`,
      };
    },
    noPrevious() {
      return this.currentPosition === 0;
    },
    noNext() {
      return this.currentPosition === this.maxPosition;
    },
    disablePrevious() {
      return !this.wrapChildren && this.noPrevious;
    },
    disableNext() {
      return !this.wrapChildren && this.noNext;
    },
  },
  watch: {
    min(newValue) {
      this.minChildrenDisplayed = newValue;
      this._updateContentWidth();
    },
    max(newValue) {
      this.maxChildrenDisplayed = newValue;
      this._updateContentWidth();
    },
    position(newValue) {
      if (newValue !== this.currentPosition) {
        this._slideTo(newValue);
      }
    },
    wrap(newValue) {
      this.wrapChildren = newValue;
    },
    autoplay(newValue) {
      this.autoplayNext = newValue;
    },
    hideControls() {
      this.showControls = !this.hideControls;
    },
    currentPosition(newValue) {
      this.$emit('position-changed', newValue);
    },
    maxPosition(newValue) {
      this.$emit('max-position-changed', newValue);
    },
    spacing() {
      this._updateContentWidth();
    },
    async uniqueResetKey() {
      await this.$nextTick();
      this._updateContentWidth();
    },
  },
  mounted() {
    window.addEventListener('resize', this._resetContentWidth);
    this.$nextTick(async () => {
      this._resetContentWidth();
      // TODO: make vertical/showControls work without waiting for another tick
      await this.$nextTick();
      this._updateContentWidth();
      // listens to any added or removed children (for async items)
      if (this.$refs.content) {
        this.contentObserver = new MutationObserver(this._updateContentWidth);
        this.contentObserver.observe(this.$refs.content, { childList: true });
      }
    });

    if (this.autoplayNext) {
      this.wrapChildren = true;
      this.autoplayIntervalId = setInterval(() => {
        if (this.autoplayNext) {
          this.next();
        } else {
          clearInterval(this.autoplayIntervalId);
        }
      }, this.autoplayInterval);
    }
  },
  destroyed() {
    window.removeEventListener('resize', this._resetContentWidth);
    clearInterval(this.autoplayIntervalId);

    if (this.contentObserver) {
      this.contentObserver.disconnect();
    }
  },
  methods: {
    previous(event) {
      let increment = 1;
      if (event) {
        this.autoplayNext = false;
        increment = this._getSlideIncrement();
      }
      if (this.disablePrevious) {
        return;
      }
      if (increment === 0) {
        increment = 1;
      }
      this._slideTo(this.currentPosition - increment);
    },
    next(event) {
      let increment = 1;
      if (event) {
        this.autoplayNext = false;
        increment = this._getSlideIncrement();
      }
      if (this.disableNext) {
        return;
      }
      if (increment === 0) {
        increment = 1;
      }
      this._slideTo(this.currentPosition + increment);
    },
    snap() {
      this._slideTo(this.currentPosition);
    },
    handleMouseDown(event) {
      event.preventDefault();
      this.mousePressed = true;
      this.mousePositionX = event.pageX;
      this.mousePositionY = event.pageY;
    },
    handleMouseMove(event) {
      // event.which means the user is holding down the mouse button (dragging)
      if (
        this.mousePositionY > event.pageY + 5 ||
        this.mousePositionY < event.pageY - 5 ||
        this.mousePositionX > event.pageX + 5 ||
        this.mousePositionX < event.pageX - 5
      ) {
        if (!this.disableMouseDrag && this.mousePressed) {
          event.preventDefault();
          this.autoplayNext = false;
          this.grabbing = true;
          const mouseMovement = !this.vertical
            ? event.movementX
            : event.movementY;
          this._move(mouseMovement);
        }
      }
    },
    handleMouseUp(event) {
      this.mousePressed = false;
      if (this.grabbing) {
        event.preventDefault();
        event.stopPropagation();
        this.grabbing = false;
        this._checkMove();
      } else {
        this.grabbing = false;
      }
    },
    handleMouseLeave() {
      this.mousePressed = false;
      this.grabbing = false;
    },
    handleTouchMove(event) {
      if (!this.disableTouchDrag) {
        this.autoplayNext = false;
        const touch = event.targetTouches[0];
        const touchPosition = !this.vertical ? touch.pageX : touch.pageY;
        if (this.previousTouch) {
          const touchMovement = touchPosition - this.previousTouch;
          this._move(touchMovement);
        }
        this.previousTouch = touchPosition;
      }
    },
    handleTouchEnd(event) {
      if (this.previousTouch) {
        event.preventDefault();
        event.stopPropagation();
        this.previousTouch = null;
        this._checkMove();
      }
    },
    _resetContentWidth() {
      if (this.maxChildrenDisplayed < this.minChildrenDisplayed) {
        this.maxChildrenDisplayed = this.minChildrenDisplayed;
      } else if (this.minChildrenDisplayed > this.maxChildrenDisplayed) {
        this.minChildrenDisplayed = this.maxChildrenDisplayed;
      }

      if (this.$refs.content) {
        this.$refs.content.scrollLeft = 0;
      }

      this.currentPosition = 0;
      this.previousTouch = null;
      this.movement = 0;

      this._updateContentWidth();
    },
    _updateContentWidth() {
      const carousel = this.$refs.carousel;

      const content = this.$refs.content;

      // hide carousel if no child nodes
      if (!content || !content.hasChildNodes) {
        this.showCarousel = false;
        return;
      }

      const children = this._getChildren();

      if (!this.maxChildrenDisplayed) {
        this.maxChildrenDisplayed = children.length;
      }

      const child = children[0];
      const childWidth = child ? child.clientWidth : 0;
      const parentWidth = this.vertical
        ? carousel.parentElement.clientHeight
        : carousel.parentElement.clientWidth;
      const availableWidth = parentWidth - (this.showControls ? 90 : 0);
      let childrenDisplayed = child
        ? Math.floor(
            (availableWidth + this.spacing) / (childWidth + this.spacing)
          )
        : 0;

      if (childrenDisplayed > this.maxChildrenDisplayed) {
        childrenDisplayed = this.maxChildrenDisplayed;
      } else if (childrenDisplayed < this.minChildrenDisplayed) {
        childrenDisplayed = this.minChildrenDisplayed;
      }

      this.childrenDisplayed = childrenDisplayed;
      const peekAmount = this.peek ? Math.floor(childWidth / 3) : 0;
      this.contentWidth =
        childWidth > 0
          ? childWidth * childrenDisplayed +
            this.spacing * (childrenDisplayed - 1) +
            peekAmount
          : 0;

      // WARNING: 90 represents the left and right arrow control widths
      this.showControls =
        !this.hideControls &&
        !(
          this.$refs.content.scrollWidth < carousel.clientWidth - 90 &&
          this.max >= children.length
        );

      this.slideIncrement = this.childrenDisplayed;
      this.maxPosition = content.children.length - this.slideIncrement;
    },
    _slideTo(position) {
      if (this.busy) {
        return;
      }

      const maxPosition = this._getMaxPosition();
      if (position < 0) {
        if (this.currentPosition > 0 || !this.wrapChildren) {
          position = 0;
        } else {
          position = maxPosition;
        }
      } else if (position > maxPosition) {
        if (this.currentPosition < maxPosition || !this.wrapChildren) {
          position = maxPosition;
        } else {
          position = 0;
        }
      }

      const child = this._getChild(position);
      if (child) {
        this.currentPosition = position;
        this.busy = true;
        this._scrollTo(child.offsetLeft);
      }
    },
    _move(movement) {
      if (this.busy) {
        this.movement = 0;
        return;
      }

      const content = this.$refs.content;
      // movement > 0 indicates movement in the left, or previous
      const isPrevious = movement > 0 && content.scrollLeft > 0;
      // movement < 0 indicates movement in the right, or next
      const isNext =
        movement < 0 &&
        content.scrollLeft < content.scrollWidth - content.clientWidth;

      if (isPrevious || isNext) {
        content.scrollLeft -= movement;
        this.movement -= movement;
      }
    },
    _checkMove() {
      if (this.busy) {
        this.movement = 0;
        return;
      }

      if (this.multiple) {
        this._updatePosition();
      }

      if (this.movement > 5 && this.currentPosition < this.maxPosition) {
        this.next();
      } else if (this.movement < -5 && this.currentPosition > 0) {
        this.previous();
      } else {
        this.snap();
      }
      this.movement = 0;
    },
    _scrollTo(position, callback) {
      const content = this.$refs.content;
      let timer = null;

      const onScroll = () => {
        if (timer !== null) {
          clearTimeout(timer);
        }

        timer = setTimeout(() => {
          content.removeEventListener('scroll', onScroll);
          this.busy = false;

          if (callback) {
            callback();
          }
        }, 100);
      };

      content.addEventListener('scroll', onScroll);
      onScroll();
      content.scrollTo({
        left: position,
        behavior: 'smooth',
      });
    },
    _updatePosition() {
      const currentChild = this._getChild(this.currentPosition);
      if (currentChild) {
        const movementThreshold = currentChild.scrollWidth;
        let positionChange = Math.floor(this.movement / movementThreshold);
        if (positionChange <= -1) {
          // WARNING: This is to prevent an extra move occuring when going
          // backwards
          positionChange++;
        }
        const newPosition = this.currentPosition + positionChange;
        const maxPosition = this._getMaxPosition();
        if (newPosition < 0) {
          this.currentPosition = 0;
        } else if (newPosition > maxPosition) {
          this.currentPosition = maxPosition;
        } else {
          this.currentPosition = newPosition;
        }
      }
    },
    _getChildren() {
      const content = this.$refs.content;
      return content.children || content.childNodes;
    },
    _getChild(position) {
      const content = this.$refs.content;
      const children = this._getChildren();
      if (position >= 0 && position < children.length) {
        return content.children[position];
      }
      return null;
    },
    _getSlideIncrement() {
      // WARNING: clientWidth not available after first tick, this was made to
      // get around it
      const content = this.$refs.content;
      const child = content.childNodes[0];
      const childWidth = child.scrollWidth;
      this.slideIncrement = Math.floor(content.clientWidth / childWidth);
      return this.slideIncrement;
    },
    _getMaxPosition() {
      const children = this._getChildren();
      this.maxPosition = children.length - this.slideIncrement;
      return this.maxPosition;
    },
  },
};
</script>

<style lang="scss" scoped>
* {
  box-sizing: border-box;
}

.ns-carousel {
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;

  &.ns-carousel-multiple {
    .ns-carousel-content {
      // display: block;
    }
  }

  &.ns-carousel-vertical {
    transform: rotate(90deg);
    transform-origin: top left;
    left: 100%;
    width: min-content;
  }
}

.ns-carousel-control {
  flex-shrink: 0;
  width: 45px;
  opacity: 0.4;
  transition: opacity 0.15s ease-in-out;
  cursor: pointer;

  &:hover {
    opacity: 1;
  }

  &:disabled,
  &[disabled],
  &.disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
}

.ns-carousel-left {
  padding-left: 15px;
}

.ns-carousel-right {
  padding-right: 15px;
}

.ns-carousel-content-window {
  &.ns-carousel-grabbing {
    cursor: grabbing;

    .ns-carousel-content ::v-deep * {
      pointer-events: none;
    }
  }
}

.ns-carousel-content {
  position: relative;
  flex: 1;
  display: flex;
  overflow: hidden;
  user-drag: none;
  -webkit-user-drag: none;
  user-select: none;
  pointer-events: none;

  ::v-deep * {
    user-drag: none;
    -webkit-user-drag: none;
    user-select: none;
    pointer-events: all;
  }
}

.ns-carousel-content > * {
  box-sizing: content-box;
  white-space: normal;
  margin-right: 10px;

  &:last-child {
    margin-right: 0;
  }
}
</style>
